import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { UserCheck, Loader2, CheckCircle2, Utensils, Phone, ArrowUpCircle, Mail, Send, X, ScanLine, BarChart3 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import TicketScanner from "@/components/pos/TicketScanner";
import { money } from "@/lib/format";

function MessageComposer({ eventId, tiers, onClose }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("confirmed");
  const [sending, setSending] = useState(false);
  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/events/${eventId}/message`, { subject, body, audience });
      toast.success(`Sent to ${data.sent}/${data.recipients} attendees${data.sent === 0 ? " (email not configured?)" : ""}`);
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center px-4 bg-black/40 backdrop-blur-sm" data-testid="message-composer">
      <div className="relative w-full max-w-md bg-[#F9F7F3] rounded-3xl shadow-2xl p-7">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#4A5D6B]"><X size={20} /></button>
        <h3 className="font-serif text-2xl text-[#1A242B] mb-4">Message attendees</h3>
        <label className="text-sm font-medium text-[#1A242B] mb-1 block">Audience</label>
        <select value={audience} onChange={(e) => setAudience(e.target.value)} data-testid="message-audience" className="eyw-input w-full mb-3">
          <option value="confirmed">Confirmed attendees</option>
          <option value="waitlisted">Waitlist only</option>
          <option value="all">Everyone (incl. waitlist)</option>
          {tiers.map((t) => <option key={t.name} value={t.name}>Tier: {t.name}</option>)}
        </select>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" data-testid="message-subject" className="eyw-input w-full mb-3" />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} placeholder="Your message…" data-testid="message-body" className="eyw-input w-full resize-none mb-4" />
        <button onClick={send} disabled={sending || !subject.trim() || !body.trim()} data-testid="message-send" className="eyw-btn w-full disabled:opacity-50">
          {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={15} />} Send
        </button>
      </div>
    </div>
  );
}

function SalesBreakdown({ eventId }) {
  const { data } = useQuery({
    queryKey: ["pos-ticket-sales", eventId],
    queryFn: () => api.get(`/pos/events/${eventId}/ticket-sales`).then((r) => r.data),
    enabled: !!eventId,
  });
  if (!data) return null;
  const sections = [
    { label: "Ticket tiers", rows: data.tiers },
    { label: "Meals", rows: data.meals },
    { label: "Accommodation", rows: data.lodging },
  ].filter((s) => s.rows.length > 0);
  if (sections.length === 0) return null;
  return (
    <div className="eyw-card p-5 mb-6" data-testid="sales-breakdown">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="font-serif text-lg text-[#1A242B] inline-flex items-center gap-2"><BarChart3 size={16} className="text-[#144F75]" /> Sales breakdown</h3>
        <p className="text-sm text-[#4A5D6B]" data-testid="sales-totals">
          Booked <span className="font-semibold text-[#144F75]">{money(Number(data.totals.booked))}</span>
          {" · "}Collected <span className="font-semibold text-[#5C8A72]">{money(Number(data.totals.collected))}</span>
          {" · "}{data.totals.seats} seat{data.totals.seats === 1 ? "" : "s"}
        </p>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {sections.map((s) => (
          <div key={s.label}>
            <p className="text-xs uppercase tracking-wide text-[#8FA89B] mb-1.5">{s.label}</p>
            <div className="space-y-1">
              {s.rows.map((r) => (
                <div key={r.name} className="flex items-center justify-between text-sm rounded-lg bg-[#F9F7F3] px-3 py-1.5" data-testid={`sales-row-${s.label === "Ticket tiers" ? "tier" : s.label === "Meals" ? "meal" : "lodging"}-${r.name}`}>
                  <span className="text-[#1A242B] truncate mr-2">
                    {r.name}
                    {r.closes_on && <span className="text-[10px] text-[#B5794E] ml-1.5">ends {r.closes_on}</span>}
                  </span>
                  <span className="text-[#4A5D6B] whitespace-nowrap">
                    {r.sold}{r.capacity > 0 ? `/${r.capacity}` : ""} · {money(Number(r.revenue))}
                    {r.waitlisted > 0 && <span className="text-[#E0A458]"> +{r.waitlisted} WL</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function POSEvents() {
  const qc = useQueryClient();
  const [eventId, setEventId] = useState("");
  const [msgOpen, setMsgOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const events = useQuery({ queryKey: ["pos-events"], queryFn: () => api.get("/pos/events-upcoming").then((r) => r.data) });
  const att = useQuery({
    queryKey: ["pos-attendees", eventId],
    queryFn: () => api.get(`/pos/events/${eventId}/attendees`).then((r) => r.data),
    enabled: !!eventId,
  });

  const checkin = async (id) => {
    await api.put(`/pos/registrations/${id}/checkin`, {});
    qc.invalidateQueries({ queryKey: ["pos-attendees", eventId] });
    toast.success("Checked in");
  };
  const promote = async (id) => {
    await api.post(`/registrations/${id}/promote`);
    qc.invalidateQueries({ queryKey: ["pos-attendees", eventId] });
    toast.success("Promoted from waitlist");
  };

  const currentEvent = (events.data || []).find((e) => e.id === eventId);
  const tiers = currentEvent?.tiers || [];

  return (
    <div data-testid="pos-events">
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <select value={eventId} onChange={(e) => setEventId(e.target.value)} data-testid="event-select" className="eyw-input w-full max-w-md">
          <option value="">Select an event / retreat…</option>
          {(events.data || []).map((e) => <option key={e.id} value={e.id}>{`${e.title} — ${e.date}`}</option>)}
        </select>
        <button onClick={() => setScanOpen(true)} data-testid="scan-tickets-btn" className="eyw-btn !py-2 text-sm"><ScanLine size={15} /> Scan tickets</button>
        {eventId && <button onClick={() => setMsgOpen(true)} data-testid="message-attendees-btn" className="eyw-btn-ghost !py-2 text-sm"><Mail size={15} /> Message attendees</button>}
      </div>

      {!eventId ? <p className="text-[#4A5D6B] text-sm">Choose an event to see its attendee list and check people in.</p>
        : att.isLoading || !att.data ? <div className="py-12 text-center text-[#4A5D6B]"><Loader2 className="animate-spin inline" /></div>
        : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6 max-w-2xl">
              <div className="eyw-card p-4 text-center"><div className="text-2xl font-serif text-[#144F75]">{att.data.stats.total}</div><p className="text-xs text-[#4A5D6B]">Registered</p></div>
              <div className="eyw-card p-4 text-center"><div className="text-2xl font-serif text-[#5C8A72]">{att.data.stats.checked_in}</div><p className="text-xs text-[#4A5D6B]">Checked in</p></div>
              <div className="eyw-card p-4 text-center"><div className="text-2xl font-serif text-[#144F75]">{att.data.stats.paid}</div><p className="text-xs text-[#4A5D6B]">Paid</p></div>
              <div className="eyw-card p-4 text-center"><div className="text-2xl font-serif text-[#E0A458]">{att.data.stats.waitlisted}</div><p className="text-xs text-[#4A5D6B]">Waitlisted</p></div>
            </div>
            <SalesBreakdown eventId={eventId} />
            <div className="eyw-card overflow-hidden hidden md:block" data-testid="attendee-list">
              <table className="w-full text-sm">
                <thead className="bg-[#F9F7F3] text-[#4A5D6B]"><tr>
                  <th className="text-left px-4 py-3">Attendee</th><th className="text-left px-4 py-3">Tier</th>
                  <th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Meal</th>
                  <th className="text-left px-4 py-3">Lodging</th><th className="text-left px-4 py-3">Emergency</th><th className="text-right px-4 py-3">Action</th>
                </tr></thead>
                <tbody>
                  {att.data.attendees.filter((a) => a.status !== "cancelled").map((a) => (
                    <tr key={a.id} className="border-t border-[#E5E0D8]" data-testid={`attendee-${a.id}`}>
                      <td className="px-4 py-3 text-[#1A242B]">{a.name || a.user_email}</td>
                      <td className="px-4 py-3 text-[#4A5D6B]">{a.tier || "—"}</td>
                      <td className="px-4 py-3">
                        {a.status === "waitlisted"
                          ? <span className="text-xs px-2 py-0.5 rounded-full bg-[#E0A458]/15 text-[#B5794E]">waitlisted</span>
                          : <span className={`text-xs px-2 py-0.5 rounded-full ${a.payment_status === "paid" ? "bg-[#144F75]/10 text-[#144F75]" : a.payment_status === "free" ? "bg-[#5C8A72]/15 text-[#5C8A72]" : "bg-[#D98E73]/15 text-[#B5794E]"}`}>{a.payment_status === "unpaid" ? "unpaid" : a.status}</span>}
                      </td>
                      <td className="px-4 py-3 text-[#4A5D6B]"><Utensils size={13} className="inline mr-1 text-[#8FA89B]" />{a.meal_option || a.meal_preference || "—"}</td>
                      <td className="px-4 py-3 text-[#4A5D6B]" data-testid={`attendee-lodging-${a.id}`}>{a.lodging_option || a.lodging || "—"}</td>
                      <td className="px-4 py-3 text-[#4A5D6B]">{a.emergency_contact_name ? <span><Phone size={12} className="inline mr-1" />{a.emergency_contact_name}</span> : "—"}</td>
                      <td className="px-4 py-3 text-right">
                        {a.status === "waitlisted"
                          ? <button onClick={() => promote(a.id)} data-testid={`attendee-promote-${a.id}`} className="eyw-btn-ghost !py-1 !px-3 text-xs"><ArrowUpCircle size={13} /> Promote</button>
                          : a.checked_in ? <span className="text-[#5C8A72] text-xs inline-flex items-center gap-1"><CheckCircle2 size={14} /> in</span>
                            : <button onClick={() => checkin(a.id)} data-testid={`attendee-checkin-${a.id}`} className="eyw-btn-ghost !py-1 !px-3 text-xs"><UserCheck size={13} /> Check in</button>}
                      </td>
                    </tr>
                  ))}
                  {att.data.attendees.filter((a) => a.status !== "cancelled").length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-[#4A5D6B]">No registrations for this event yet.</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-3" data-testid="attendee-list-mobile">
              {att.data.attendees.filter((a) => a.status !== "cancelled").map((a) => (
                <div key={a.id} data-testid={`attendee-card-${a.id}`} className="eyw-card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[#1A242B] font-medium break-words">{a.name || a.user_email}</p>
                      {a.tier && <p className="text-xs text-[#8FA89B]">Tier: {a.tier}</p>}
                    </div>
                    {a.status === "waitlisted"
                      ? <span className="text-xs px-2 py-0.5 rounded-full bg-[#E0A458]/15 text-[#B5794E] shrink-0">waitlisted</span>
                      : <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${a.payment_status === "paid" ? "bg-[#144F75]/10 text-[#144F75]" : a.payment_status === "free" ? "bg-[#5C8A72]/15 text-[#5C8A72]" : "bg-[#D98E73]/15 text-[#B5794E]"}`}>{a.payment_status === "unpaid" ? "unpaid" : a.status}</span>}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#4A5D6B]">
                    <span><Utensils size={13} className="inline mr-1 text-[#8FA89B]" />{a.meal_option || a.meal_preference || "—"}</span>
                    {(a.lodging_option || a.lodging) && <span>{a.lodging_option || a.lodging}</span>}
                    {a.emergency_contact_name && <span><Phone size={12} className="inline mr-1" />{a.emergency_contact_name}</span>}
                  </div>
                  <div className="mt-3">
                    {a.status === "waitlisted"
                      ? <button onClick={() => promote(a.id)} data-testid={`attendee-promote-${a.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><ArrowUpCircle size={13} /> Promote</button>
                      : a.checked_in ? <span className="text-[#5C8A72] text-xs inline-flex items-center gap-1"><CheckCircle2 size={14} /> Checked in</span>
                        : <button onClick={() => checkin(a.id)} data-testid={`attendee-checkin-${a.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><UserCheck size={13} /> Check in</button>}
                  </div>
                </div>
              ))}
              {att.data.attendees.filter((a) => a.status !== "cancelled").length === 0 && <p className="px-4 py-10 text-center text-[#4A5D6B] text-sm eyw-card">No registrations for this event yet.</p>}
            </div>
          </>
        )}
      {msgOpen && <MessageComposer eventId={eventId} tiers={tiers} onClose={() => setMsgOpen(false)} />}
      {scanOpen && <TicketScanner onClose={() => setScanOpen(false)} onCheckedIn={() => qc.invalidateQueries({ queryKey: ["pos-attendees", eventId] })} />}
    </div>
  );
}
